import { DialogOpenResult, DialogService, DialogSettings } from "aurelia-dialog";
import { DialogCloseResult } from "aurelia-dialog/src/dialog-result";
import { autoinject, PLATFORM, useView } from "aurelia-framework";
import { Router } from "aurelia-router";

import { EventAggregator, Subscription } from "../../../node_modules/aurelia-event-aggregator";
import { OidcSignOut } from "../../common/oidc/oidc-event-handler";
import { IGetAccountsModel } from "../../interfaces/i-get-accounts";
import { Alert, IAlertOptions } from "../../resources/dialogs/alert/alert";
import { SwitchAccountDialog, SwitchAccountDialogParams } from "../../resources/dialogs/switch-account/switch-account";
import { AccountsService } from "../../services/accounts-service";
import { AccountEvent, AuthService } from "../../services/auth-service";
import { ProvidersService } from "../../services/providers-service";
import { IGetProviderDefault } from "../../services/i-providers-service";
import { BranchesService } from "../../services/branches-service";
import { PalliativePatientWarning } from "../home/my-dashboard/palliative-patient-warning";
import Infrastructure from "../../common/infrastructure";
import { config } from "../../common/config";
import { AxxessTakeover } from "../app/axxess-takeover";
import { CurrentUserService } from "../../services/current-user-service";
import { EnumsService } from "../../services/enums-service";
import { IEnumResponse } from "../../interfaces/i-enum";
import { getMissingFlags } from "../../common/utilities/bitwise";
import { IGetCurrentUserResult } from "../../interfaces/i-user";
import { AgileRotatingTakeover, IAgileTakeoverPromptOptions } from "../app/agile-rotating-takeover";

// import { getLogger } from "../../../node_modules/aurelia-logging";
@autoinject()
@useView(PLATFORM.moduleName("./select-account-view.html"))
export class SelectAccounts {
    // private readonly _logger = getLogger("select-accounts-page");
    private readonly _router: Router;
    private readonly _authService: AuthService;
    private readonly _accountService: AccountsService;
    private readonly _dialogService: DialogService;
    private readonly _currentUserService: CurrentUserService;
    private readonly _ea: EventAggregator;
    private _providersService: ProvidersService;
    private _branchesService: BranchesService;
    private _accountLoadSubscription: Subscription;
    private _enumsService: EnumsService;
    public fragment: string;
    public query: string;
    public accounts: IGetAccountsModel[];
    public warningStartDate: string = "2024-01-28T08:00:00";
    public warningEndDate: string = "2024-01-30T08:00:00";
    public libertyAgency: string = config.libertyAccountId;
    private takeOverScreenEnums: IEnumResponse[] = [];

    public constructor(
        authService: AuthService,
        router: Router,
        accountService: AccountsService,
        dialogService: DialogService,
        ea: EventAggregator,
        providersService: ProvidersService,
        branchesService: BranchesService,
        currentUserService: CurrentUserService,
        enumsService: EnumsService
    ) {
        this._authService = authService;
        this._router = router;
        this._accountService = accountService;
        this._dialogService = dialogService;
        this._ea = ea;
        this._providersService = providersService;
        this._branchesService = branchesService;
        this._currentUserService = currentUserService;
        this._enumsService = enumsService;
    }

    public async activate(params: any) {
        params = params || {};
        this.fragment = params.fragment;
        this.query = params.query;
    }

    public async attached() {
        this.accounts = await this._accountService.getAllAccounts();
        await this.showAxxessTakeover();
        await this.switchAccounts();
        await this.initSubscriptions();
    }

    public async showAxxessTakeover() {
        const currentUser = await this._currentUserService.getUser();
        await this.showRotatingAgileTakeover(currentUser);
        this.takeOverScreenEnums = await this._enumsService.getAxxessTakeOverEnum();
        const missingFlags = getMissingFlags(currentUser.takeOverScreenFlags, this.takeOverScreenEnums);
        const shouldShow = missingFlags.length > 0 && !currentUser.isSupportUser;
        if (!shouldShow) {
            return;
        }
        const gen = this.autoTakeoverGenerator(missingFlags);
        await this.runTakeoverGenerator(gen);
    }

    public async showRotatingAgileTakeover(currentUser: IGetCurrentUserResult) {
        let showAgileTakeover = !currentUser.hasShownAgileTakeover && !currentUser.isSupportUser;
        if (
            showAgileTakeover &&
            currentUser.landingPageInfo &&
            (currentUser.landingPageInfo.pageNumber !== undefined || currentUser.landingPageInfo.pageNumber !== null)
        ) {
            await this.showAgileTakeover(currentUser.landingPageInfo.pageNumber);
            return;
        }
    }

    public async showAgileTakeover(pageNumber: number) {
        let agileTakeoverPromptOptions: IAgileTakeoverPromptOptions = {
            takeoverIndex: pageNumber
        };
        let dialogOptions: DialogSettings = {
            viewModel: AgileRotatingTakeover,
            lock: false,
            model: agileTakeoverPromptOptions
        };
        let dialog = (await this._dialogService.open(dialogOptions)) as DialogOpenResult;
        let closeResult = await dialog.closeResult;
        if (!closeResult.wasCancelled) {
            return;
        }
    }

    public async switchAccounts() {
        if (this.accounts?.length === 0) {
            this.handleNoAccounts();
        } else if (this.accounts?.length === 1) {
            let account = this.accounts[0];
            if (account.providers?.length > 1) {
                await this.handleMultipleAccounts(account.id);
            } else {
                let providerId = [account.providers[0].id];
                let lineOfService = account.providers[0].lineOfService;
                this._authService.setProviderInformation(providerId, lineOfService);
                await this._authService.setAccountId(account.id, account.clusterId);
            }
        } else {
            let accountId = await this.getAccountIdFromQuery();
            if (!accountId) {
                await this.handleMultipleAccounts(this._authService.getAccountId());
            }
        }
    }

    public async initSubscriptions() {
        this._accountLoadSubscription = this._ea.subscribe(AccountEvent.Loaded, () => {
            this.navigateToNewAccount();
        });
        this._ea.publish(AccountEvent.Changed);
    }

    public navigateToNewAccount() {
        let currentUrl = window.location.origin;
        let clusterId = this._authService.getClusterId();
        let newUrl = "";
        if (!!clusterId) {
            // After "hospice" or "hospice-feature", insert clusterId
            newUrl = currentUrl.replace(/(hospice(?:-feature)?)(\d+)?(?=\.)/, `$1${clusterId}`);
        }
        if (currentUrl !== newUrl) {
            console.log("Switch to new cluster:", { old: currentUrl, new: newUrl });
            let accountId = this._authService.getAccountId();
            this._router.navigate(`${newUrl}?accountId=${accountId}`);
        } else {
            console.debug(`Restoring saved location:`, this.fragment);
            this._router.navigate(this.fragment);
        }
    }

    private async handleNoAccounts() {
        let model: IAlertOptions = {
            title: `Authorization Failed`,
            message:
                "You do not have any agencies linked to your account. If you believe this is an error, please contact support."
        };

        let options: DialogSettings = {
            viewModel: Alert,
            model: model
        };
        await this._dialogService.open(options).whenClosed(async () => {
            this._ea.publish(OidcSignOut);
        });
    }

    private async handleMultipleAccounts(currentAccountId: string) {
        let params: SwitchAccountDialogParams = {
            accounts: this.accounts,
            currentAccountId: currentAccountId
        };

        let options: DialogSettings = {
            viewModel: SwitchAccountDialog,
            model: params
        };

        await this._dialogService.open(options).whenClosed(async (selectedInfo: DialogCloseResult) => {
            // This means user switched from one account to another within AxxessHospice
            // As per Product and the team's consensus, when user switches to an agency internally
            // Take the user to dashboard
            this.fragment = "/home/my-dashboard";
            let accountId = selectedInfo?.output?.accountId;
            let clusterId = selectedInfo?.output?.clusterId;
            let providers = selectedInfo?.output?.providers;
            let rememberedSelection = selectedInfo?.output?.rememberSelection;
            let lineOfService = selectedInfo.output?.lineOfService;
            this.handleClosedAccountDialog(accountId, providers, rememberedSelection, lineOfService, clusterId);
        });
    }

    private async handleClosedAccountDialog(
        accountId: string,
        providers: IGetProviderDefault[],
        rememberSelection: boolean,
        lineOfService: number,
        clusterId: string
    ) {
        if (rememberSelection) {
            await this._providersService.updateProviderDefaults(providers);
        }
        let defaultProviders = providers.filter((provider: IGetProviderDefault) => provider.isDefault);
        let selectedProviders =
            defaultProviders?.length > 0
                ? defaultProviders
                : providers.filter((provider: IGetProviderDefault) => provider.lineOfService === lineOfService);
        let selectedProviderIds = selectedProviders.map((provider: IGetProviderDefault) => provider.id);
        this._authService.setProviderInformation(selectedProviderIds, lineOfService);
        this._branchesService.clearBranchCache();
        this._accountService.clearLineOfService();
        this._authService.setAccountId(accountId, clusterId);
    }

    private async getAccountIdFromQuery() {
        // TODO: Update method names to reflect new provider changes
        if (!this.query) {
            return null;
        }
        let queryParams = this.query.split("&");
        let accountIdQuery = queryParams.filter((param) => param.includes("accountId"))[0];
        if (!accountIdQuery) {
            return null;
        }

        let accountId = accountIdQuery.split("=")[1];
        console.log("Select accounts: AccountId from URL: ", accountId);
        this.query = queryParams.filter((param) => !param.includes(accountIdQuery)).join("&");
        console.log("Query after removing accountId: ", this.query);
        if (!accountId) {
            return null;
        }
        let account = this.accounts.find((f) => f.id == accountId);
        if (!account || !account.providers) {
            return null;
        }
        let clusterId = account.clusterId;
        let providers = account.providers;
        let defaultProviders = providers.length === 1 ? providers : providers.filter((provider) => provider.isDefault);
        if (defaultProviders.length > 0) {
            // If user has rememberedSelection do not not show dialog and set provider information.
            let providerIds = defaultProviders.map((provider) => provider.id);
            let lineOfService = defaultProviders[0].lineOfService;
            this._authService.setProviderInformation(providerIds, lineOfService);
            this._authService.setAccountId(accountId, clusterId);
        } else {
            await this.handleMultipleAccounts(accountId);
        }
        return accountId;
    }

    private async openPalliativePatientWarning() {
        // ToDo: Remove this function when no longer necessary.
        let isProd = Infrastructure.isProdEnvironment;
        if (!isProd) {
            this.warningStartDate = "2024-01-24T08:00:00";
            this.warningEndDate = "2024-01-28T08:00:00";
        }
        let todaysDate = new Date();
        let todaysDateEST = todaysDate.toLocaleString("en-US", { timeZone: "EST" });
        if (
            new Date(todaysDateEST) > new Date(this.warningEndDate) ||
            new Date(todaysDateEST) < new Date(this.warningStartDate)
        ) {
            return;
        }
        let dialogOptions: DialogSettings = {
            viewModel: PalliativePatientWarning
        };
        await this._dialogService.open(dialogOptions);
    }

    private *autoTakeoverGenerator(missingFlags: IEnumResponse[]) {
        for (let flag of missingFlags) {
            yield {
                viewModel: AxxessTakeover,
                model: flag
            };
        }
    }

    private async runTakeoverGenerator(gen: Generator<any>) {
        for (let step of gen) {
            let dialog = await this._dialogService.open(step);
            let result = await dialog.closeResult;
            if (!result || result.wasCancelled) {
                return false;
            }
        }
        return true;
    }

    public detached() {
        let accoundId = this._authService.getAccountId();
        if (accoundId == this.libertyAgency) {
            this.openPalliativePatientWarning();
        }
        this._accountLoadSubscription?.dispose();
    }
}
